import { FC, ReactNode } from 'react'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'

export interface PageWrapperProps {
    /** Content rendered before the title in the toolbar (e.g. sidebar toggle button) */
    toolbarLeft?: ReactNode
    /** Page title or breadcrumb content rendered in the toolbar */
    title?: ReactNode
    /** Action buttons rendered on the right side of the toolbar */
    actions?: ReactNode
    /** Content rendered above the toolbar (e.g. license warning banners) */
    banner?: ReactNode
    /** Page content */
    children: ReactNode
    /** When true, removes the maxWidth constraint */
    fullWidth?: boolean
}

/**
 * Common page wrapper providing consistent layout structure:
 * - Section with maxWidth: 1920px (unless fullWidth)
 * - Sticky AppBar toolbar with title and actions
 * - Content area with px: 3, py: 2 padding
 */
const PageWrapper: FC<PageWrapperProps> = ({ toolbarLeft, title, actions, banner, children, fullWidth }) => {
    const sectionStyles = {
        position: 'relative' as const,
        width: '100%',
        ...(fullWidth ? {} : { maxWidth: '1920px' }),
    }

    return (
        <section style={sectionStyles}>
            {banner}
            <AppBar position="sticky" data-test-id="app-bar" color="transparent">
                <Toolbar
                    disableGutters
                    sx={{
                        backgroundColor: (theme) => theme.palette.background.default,
                        borderBottomWidth: '1px',
                        borderBottomColor: (theme) => theme.palette.divider,
                        borderBottomStyle: 'solid',
                        px: 3,
                    }}
                >
                    {toolbarLeft}
                    {title}
                    <Box sx={{ flexGrow: 1 }} />
                    {actions}
                </Toolbar>
            </AppBar>
            <Box sx={{ px: 3, py: 2 }}>{children}</Box>
        </section>
    )
}

export default PageWrapper
